import { z } from 'zod';

/**
 * B.1 — Briefing-Up view + tokenized read-only URL + briefing subscriptions
 * (operator-cop-doctrine §2.6 elected-official persona, §3B.7 render profile,
 * §6.3 hybrid decision: in-app mode switch + tokenized read-only URL + emailed
 * PDF at operational-period boundaries).
 *
 * The Briefing-Up surface is GENERATED, not authored — composed server-side from
 * the live Response Levels (high-risk hotspots), active OC fires, community
 * human-impact rollups, and the most recent SITREP. These contracts cover three
 * things: the snapshot payload the render profile consumes, the tokenized URL the
 * Minister's office opens, and the per-incident email subscription list the v3.2
 * scheduler auto-pushes to.
 */

/* ------------------------------------------------------------------ *
 * Briefing snapshot — the generated Briefing-Up data payload
 * ------------------------------------------------------------------ */

/** One simplified, large-labelled map marker for the briefing map (only L3+
 *  fused hotspots + active OC fires render — ambient layers off, §3B.7). */
export const BriefingMapItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['high_risk_hotspot', 'oc_fire']),
  lat: z.number(),
  lon: z.number(),
  label: z.string(),
  /** 1–4 for fused hotspots; null for OC fires (unscored). */
  response_level: z.number().int().nullable(),
  /** Nearest population context, e.g. "near Thunder Bay" — affected-population
   *  context the elected official needs; null when no centre is in range. */
  affected_population_context: z.string().nullable(),
});
export type BriefingMapItem = z.infer<typeof BriefingMapItemSchema>;

/** One Response-Level section of the briefing spine (L4 first, L3 next, …). */
export const BriefingSectionSchema = z.object({
  response_level: z.number().int(),
  level_name: z.string(),
  /** The one headline metric for this level (density-inverted, one per band). */
  headline: z.string(),
  items: z.array(
    z.object({
      label: z.string(),
      detail: z.string(),
    }),
  ),
});
export type BriefingSection = z.infer<typeof BriefingSectionSchema>;

/** The latest SITREP distilled to the narrative-first content (executive summary
 *  + Recommended Actions are the primary content; map is the supporting visual). */
export const BriefingLatestSitrepSchema = z.object({
  id: z.string(),
  title: z.string(),
  created_at: z.string(),
  incident_id: z.string().nullable(),
  response_level_max: z.number().int().nullable(),
  report_version: z.string(),
  /** The full rendered narrative (markdown) — the render profile shows the
   *  executive-summary + Recommended-Actions sections large; the rest collapses. */
  summary_markdown: z.string(),
  /** Pre-extracted Recommended Actions bullets (parsed from the narrative). */
  recommended_actions: z.array(z.string()),
});
export type BriefingLatestSitrep = z.infer<typeof BriefingLatestSitrepSchema>;

export const BriefingSnapshotSchema = z.object({
  generated_at: z.string(),
  /** Human scope label (incident name + number, or the region). */
  scope_label: z.string(),
  /** The incident this snapshot is bound to (incident/token scope), else null. */
  incident_id: z.string().nullable(),
  /** Max Response Level across in-scope fused hotspots (the one-glance severity). */
  response_level_max: z.number().int().nullable(),
  /** One headline metric per hazard ("3 communities at Response Level 4 ·
   *  11,069 evacuees · 18 active OC fires") — the density-inverted top line. */
  headline_metrics: z.array(z.string()),
  /** Response-Level spine: sections ordered L4 → L1. */
  sections: z.array(BriefingSectionSchema),
  /** Simplified map markers (L3+ hotspots + OC fires only). */
  map_items: z.array(BriefingMapItemSchema),
  /** The narrative-first content — the most recent SITREP in scope (or null). */
  latest_sitrep: BriefingLatestSitrepSchema.nullable(),
  /** True when served via a read-only token (no actions / toggles / audit). */
  read_only: z.boolean(),
});
export type BriefingSnapshot = z.infer<typeof BriefingSnapshotSchema>;

/** Query for the operator-mode snapshot: a current-map-view bbox OR an incident. */
export const BriefingSnapshotQuerySchema = z
  .object({
    bbox: z.string().optional(),
    incident_id: z.string().uuid().optional(),
  })
  .refine((v) => !!v.bbox || !!v.incident_id, {
    message: 'bbox or incident_id is required',
  });

/* ------------------------------------------------------------------ *
 * Tokenized read-only URL (Minister's office)
 * ------------------------------------------------------------------ */

/** Default token lifetime; renewable. Capped at a week so a stray link can't
 *  outlive the incident. */
export const BRIEFING_TOKEN_DEFAULT_TTL_HOURS = 24;
export const BRIEFING_TOKEN_MAX_TTL_HOURS = 168;

export const BriefingTokenCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** Optional incident scope — when set the token's payload is locked to that
   *  incident; when null the token serves the whole-province current view. */
  incident_id: z.string().uuid().nullable().optional(),
  /** TTL in hours (default 24, max 168). */
  ttl_hours: z.number().int().positive().max(BRIEFING_TOKEN_MAX_TTL_HOURS).optional(),
  /** Operator-facing label, e.g. "Minister's office — Spring 2026 fires". */
  label: z.string().max(200).optional(),
});
export type BriefingTokenCreate = z.infer<typeof BriefingTokenCreateSchema>;

export const BriefingTokenSchema = z.object({
  id: z.string(),
  token: z.string(),
  /** Deep-link URL the Minister's office opens (e.g. https://app/briefing/<token>). */
  url: z.string(),
  incident_id: z.string().nullable(),
  label: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string(),
  created_by_operator_id: z.string(),
  accessed_at: z.string().nullable(),
  accessed_count: z.number().int(),
  revoked_at: z.string().nullable(),
  /** Convenience: not revoked AND not expired. */
  active: z.boolean(),
});
export type BriefingToken = z.infer<typeof BriefingTokenSchema>;

/* ------------------------------------------------------------------ *
 * Briefing subscriptions (auto-email at operational-period boundaries)
 * ------------------------------------------------------------------ */

export const BRIEFING_SUBSCRIBER_ROLES = ['minister', 'mayor', 'agency_head', 'other'] as const;
export const BriefingSubscriberRoleSchema = z.enum(BRIEFING_SUBSCRIBER_ROLES);
export type BriefingSubscriberRole = z.infer<typeof BriefingSubscriberRoleSchema>;

export const BriefingSubscriptionCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  incident_id: z.string().uuid(),
  email_address: z.string().email().max(320),
  role: BriefingSubscriberRoleSchema,
});
export type BriefingSubscriptionCreate = z.infer<typeof BriefingSubscriptionCreateSchema>;

export const BriefingSubscriptionSchema = z.object({
  id: z.string(),
  incident_id: z.string(),
  email_address: z.string(),
  role: BriefingSubscriberRoleSchema,
  subscribed_at: z.string(),
  unsubscribed_at: z.string().nullable(),
});
export type BriefingSubscription = z.infer<typeof BriefingSubscriptionSchema>;
